import { useEffect, useRef } from 'react';
import type { Nullable } from 'corgi-types';

import { useIsObserved } from '../../hooks/intersection-observer';
import { dispatchTrackingEventGA4 } from '../../tracking/dispatchTrackingEventGA4';

interface UseTrackImpression {
  trackImpression: boolean;
  element: Nullable<Element>;
  messageId: number;
  message: string;
}

const MIN_INTERSECTION_RATIO = 1;
const TRACKING_EVENT_NAME = 'checkout-step-header_view';

// Strip newlines to prevent empty values in GTM
const sanitizeMessage = (message: string) => message.replace(/\n/g, '');

const dispatchImpression = (message: string) =>
  dispatchTrackingEventGA4({
    event: TRACKING_EVENT_NAME,
    element: 'error message',
    error_message: sanitizeMessage(message),
  });

export const useTrackImpression = ({ trackImpression, messageId, message }: UseTrackImpression) => {
  const [ref, isObserved] = useIsObserved({
    threshold: MIN_INTERSECTION_RATIO,
    disconnectOnIntersection: false,
  });
  const previousMessageId = useRef(-1);

  useEffect(() => {
    if (messageId !== previousMessageId.current && trackImpression && isObserved) {
      previousMessageId.current = messageId;
      dispatchImpression(message);
    }
  }, [isObserved, message, messageId]);

  return ref;
};
